import { FC, useState, useContext, useEffect } from "react"
import PastSessionPreview from "./PastSessionPreview"
import { SessionsRequests } from "../../../api/app.service"
import { UserCtx } from "../../../context/userContext"
import DateRangePicker from "../../date-time/DateRangePicker"
import moment from "moment"
import Select from "react-select"
import useScrollToTop from "../../../hooks/useScrollToTop"
import Loader from "../../ui/loader"
import { useToast } from "../../../hooks/useToast"
import { Button } from "../../ui/button"
import { CSVLink } from "react-csv"
import { generateCsv, CsvRow } from "../../../utils/generateSessionCsv"

interface Props {
  setDetailedPastSession: any
}

const PastSessions: FC<Props> = ({ setDetailedPastSession }) => {
  const { user } = useContext(UserCtx)
  const [pastSessions, setPastSessions] = useState<any[]>([])
  const { toast } = useToast()
  const [activeDates, setActiveDates] = useState<Date[] | null>(null)
  const [onlyShowServiceSessions, setOnlyShowServiceSessions] =
    useState<boolean>(false)
  const [activeParticipantOption, setActiveParticipantOption] = useState<
    string | null
  >(null)
  const [loadingPastSessions, setLoadingPastSessions] = useState<boolean>(false)
  const datePickerDefaultStartDate = moment().subtract(30, "days")
  const datePickerDefaultEndDate = moment().endOf("day")
  const [csvData, setCsvData] = useState<CsvRow[]>([])
  const [csvHeaders, setCsvHeaders] = useState<any[]>([])

  const getPastSessions = () => {
    if (user) {
      setLoadingPastSessions(true)
      SessionsRequests.getPastSessions({
        profile_id: user.activeProfileId,
      })
        .then((data: any) => {
          const filteredByDate = filterSessionsByDate(data.session_list)
          const filteredByDateAndParticipant =
            filterSessionsByParticipant(filteredByDate)
          const filteredByDateParticipantAndService = filterSessionsByService(
            filteredByDateAndParticipant
          )

          handleGenerateCsv(filteredByDateParticipantAndService)
          setPastSessions(filteredByDateParticipantAndService)
          setLoadingPastSessions(false)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
          setLoadingPastSessions(false)
        })
    }
  }

  const filterSessionsByDate = (sessionList: any) => {
    let startDate = datePickerDefaultStartDate
    let endDate = datePickerDefaultEndDate

    if (activeDates) {
      startDate = moment(activeDates[0])
      endDate = moment(activeDates[1])
      if (startDate.isSame(endDate, "date")) {
        endDate.add(1, "day")
      }
    }

    const filteredSessionList = sessionList.filter((session: any) => {
      const sessionStartTime = moment(session.session_start_time)
      return sessionStartTime > startDate && sessionStartTime < endDate
    })

    return filteredSessionList
  }

  const filterSessionsByParticipant = (sessionList: any) => {
    if (activeParticipantOption) {
      const filteredSessionList = sessionList.filter((session: any) => {
        const participantUserIds = session.participant_list.map(
          (participant: any) => participant.user_id
        )
        return participantUserIds.includes(activeParticipantOption)
      })
      return filteredSessionList
    } else {
      return sessionList
    }
  }

  const filterSessionsByService = (sessionList: any) => {
    if (onlyShowServiceSessions) {
      const filteredSessionList = sessionList.filter((session: any) => {
        return session.service_usage_id
      })
      return filteredSessionList
    } else {
      return sessionList
    }
  }

  // Participants options logic
  const unsortedParticipantOptions = pastSessions
    ?.map((session) => {
      const participants = session.participant_list
      const participantData = participants.map((participant: any) => {
        const participantName = `${participant.first_name} ${participant.last_name}`
        const participantUserId = participant.user_id

        if (participantUserId?.toString() === user.user_id) {
          return null
        }

        return { participantName, participantUserId }
      })
      return participantData
    })
    .flat()
    .filter((e) => e !== null)
  const uniqueUserIds: Array<number> = []
  const uniqueParticipantOptions = unsortedParticipantOptions?.filter(
    (participant) => {
      if (!uniqueUserIds.includes(participant.participantUserId)) {
        uniqueUserIds.push(participant.participantUserId)
        return true
      } else {
        return false
      }
    }
  )
  const participantOptions = uniqueParticipantOptions?.map((e) => {
    return {
      value: e.participantUserId,
      label: e.participantName,
    }
  })
  const handleGenerateCsv = (sessionList: any) => {
    const { headers, data } = generateCsv(sessionList)
    setCsvHeaders(headers)
    setCsvData(data)
  }
  const handleParticipantSelectChange = (e: any) => {
    e ? setActiveParticipantOption(e.value) : setActiveParticipantOption(null)
  }

  // Service options logic
  const serviceOptions = [
    {
      value: false,
      label: "Show all",
    },
    {
      value: true,
      label: "Service Attached",
    },
  ]
  const handleServiceSelectChange = (e: any) => {
    setOnlyShowServiceSessions(e.value)
  }

  // use Effects
  useEffect(() => {
    getPastSessions()
  }, [activeDates, activeParticipantOption, onlyShowServiceSessions])

  useScrollToTop()

  return (
    <div className="px-[16px] md:px-0">
      <div className="flex flex-col -mt-4 mb-4 md:mt-0 mb:mb-0 md:flex-row items-center md:items-start justify-center md:justify-start gap-2 p-2">
        {" "}
        {csvData.length > 0 && (
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            className="h-[32px]"
            filename="CoachingLog.csv"
          >
            <Button className="h-[32px]flex items-center justify-center">
              Download CSV
            </Button>
          </CSVLink>
        )}
        {csvData.length === 0 && <p>No sessions available to export</p>}
      </div>
      <div className="mb-[24px] flex flex-col md:flex-row items-center justify-between gap-[24px]">
        <DateRangePicker
          setActiveDates={setActiveDates}
          defaultStartDate={datePickerDefaultStartDate.toDate()}
          defaultEndDate={datePickerDefaultEndDate.toDate()}
        />
        <div className="flex items-center gap-[12px]">
          <Select
            className="basic-single w-[180px] md:w-[220px] border-grayCloud text-graySlate rounded-[10px]"
            classNamePrefix="select"
            options={participantOptions}
            name="Participant"
            placeholder="Participant"
            onChange={handleParticipantSelectChange}
            isClearable={true}
          />
          <Select
            className="basic-single w-[180px] md:w-[220px] border-grayCloud text-graySlate rounded-[10px]"
            classNamePrefix="select"
            options={serviceOptions}
            name="Service"
            placeholder="Service"
            onChange={handleServiceSelectChange}
          />
        </div>
      </div>
      {loadingPastSessions ? (
        <div className="mt-[32px] flex flex-col items-center gap-[12px]">
          <Loader />
          <h3 className="font-bold text-[16px] mb-[4px] text-center">
            Looking for your past sessions...
          </h3>
        </div>
      ) : (
        <>
          {pastSessions.length > 0 ? (
            <div className="w:[378px] sm:w-[744px] mx-auto flex flex-col">
              <>
                {pastSessions &&
                  pastSessions.reverse().map((session: any) => (
                    <div
                      className="border-b last:border-0 border-grayMist last:mb-[80px] md:last:mb-0"
                      key={session.id}
                    >
                      <PastSessionPreview
                        session={session}
                        setDetailedPastSession={setDetailedPastSession}
                      />
                    </div>
                  ))}
              </>
            </div>
          ) : (
            <p className="text-graySlate font-bold mr-auto">
              No past sessions for the selected period and filters.
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default PastSessions
